export interface AiUseCase {
  useCaseId: string;
  useCaseSysId?: string;
  title: string | null;
  businessUseCase?: string;
  status: string | null;
  priority?: number;
  createdAt?: string;
  lastUpdatedDate?: string;
  overallIntakeStatus?: string;
  vpDecision?: string;
  riskAssessmentScore?: string;
  modelName?: string;
  platformName?: string;
  targetEnvironment?: string;
  intendedUsers?: string;
  requestedBy?: {
    login: string;
    displayName: string;
  };
}

// ── AI Resources ────────────────────────────────────────────────────────────

export interface GenAiModel {
  modelId: string;
  name: string;
  provider?: string;
  family?: string;
  aiCapabilityType?: string;
  deploymentStatus?: string;
  createDate?: string;
  lastUpdatedDate?: string;
  vendorTpeId?: string | null;
  openSource?: boolean;
  cloudOrOnPrem?: string;
  transparency?: string;
}

export interface McpServer {
  serverId: string;
  name: string;
  remoteUrl?: string;
  /** Array of auth methods — live schema defines this as [McpServerAuthMethod!] */
  authMethod?: string[];
  description?: string | null;
  createDate?: string;
  lastUpdatedDate?: string;
  license?: string;
  dataClassification?: string;
}

export interface AiAgent {
  agentId: string;
  name: string;
  agentType?: string;
  deploymentStatus?: string;
  levelOfAgency?: string;
  monitoringEnabled?: boolean;
  authMethod?: string;
  createDate?: string;
  lastUpdatedDate?: string;
  vendorTpeId?: string | null;
  lastLoaUpdateDate?: string;
  interactionSurface?: string;
  logEnabled?: boolean;
  bryterIntakeId?: string | null;
}

export interface ApplicationAiResources {
  applicationId: number;
  name: string;
  systemType?: string;
  aiUseCaseIds: string[];
  aiUseCases: AiUseCase[];
  aiAgents: AiAgent[];
  genAiModels: GenAiModel[];
  mcpServers: McpServer[];
}

export interface CreateAISystemFormData {
  name: string;
  description: string;
  useCaseId: string;
  processesPI?: boolean;
  processesSPI?: boolean;
  processesChildData?: boolean;
  processesPartnerData?: boolean;
  monitoringEnabled?: boolean;
}

export interface CreateAISystemResult {
  success: boolean;
  message: string;
  application?: {
    applicationId: number;
    name: string;
    systemType: string;
    useCaseIds: string[];
  };
}

/**
 * Set to true to bypass the Galileo User.aiUseCases query and return STUBBED_USE_CASES directly.
 * Flip to false once Snow's subgraph is live in the Galileo supergraph (fusionrage).
 * See: ai-inventory-design/schema/snow.graphqls — User @extends { aiUseCases }
 */
export const USE_STUBBED_USE_CASES = false;

/**
 * Stubbed approved use cases for development / mocked mode.
 * Replace with live Galileo query when available.
 */
export const STUBBED_USE_CASES: AiUseCase[] = [
  {
    useCaseId: 'UC-00001',
    title: 'Customer Support Chatbot',
    businessUseCase: 'Automated tier-1 customer support responses',
    status: 'IN_DEVELOPMENT',
    overallIntakeStatus: 'APPROVED',
    requestedBy: { login: 'jsmith', displayName: 'Jane Smith' },
  },
  {
    useCaseId: 'UC-00002',
    title: 'Code Review Assistant',
    businessUseCase:
      'AI-assisted pull request review and code quality feedback',
    status: 'APPROVED',
    overallIntakeStatus: 'APPROVED_WITH_CONDITIONS',
    requestedBy: { login: 'bjones', displayName: 'Bob Jones' },
  },
  {
    useCaseId: 'UC-00003',
    title: 'Fraud Detection Model',
    businessUseCase:
      'Real-time transaction anomaly detection for billing systems',
    status: 'IN_DEVELOPMENT',
    overallIntakeStatus: 'APPROVED',
    requestedBy: { login: 'alee', displayName: 'Alice Lee' },
  },
  {
    useCaseId: 'UC-00004',
    title: 'Content Moderation Pipeline',
    businessUseCase:
      'Automated detection and flagging of policy-violating content in user-generated uploads',
    status: 'APPROVED',
    overallIntakeStatus: 'APPROVED_WITH_CONDITIONS',
    requestedBy: { login: 'mwilliams', displayName: 'Maya Williams' },
  },
];
